import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Layers, Smartphone, ArrowUpDown, ArrowUp, ArrowDown, UserMinus, UserPlus2, Archive, AlertTriangle, CheckCircle2, CircleDashed,
} from 'lucide-react';
import { followBackApi } from '../../lib/api';
import { r14ErrorText } from '../../lib/r14Errors';
import { fmtDay, fmtInt } from '../../lib/slotStatsView';
import {
  fmtRatePct, fmtPp, slotRows, sortSlotRows, sourceColor, chartKeyOf, localDay, pendingReason, pendingShort, stuckSlots, rateOf, covered, FB_TAG,
  batchName, rateN,
} from '../../lib/followBackView';
import { ChartCard, ScrollX, Delta } from './ChartKit';
import { StateChip } from './R15Parts';
import { SlotRateChart, shortLabel } from './FollowBackCharts';
import { CountsNote, fbLog } from './FollowBackParts';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { cn } from '@/lib/utils';

/*
 * r16m (2026-10-20), design 3.7 items 6-7 (group scope only): the per-slot table + rate-by-slot chart, and the batch panel with Join /
 * Detach / Retire (design 2.4 rule 4; each behind a confirm that says what it changes; the server scopes every action to the account).
 */

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/* ------------------------------------------------------------------ by slot */

function SortHeader({ label, k, sort, onSort, align, testid, sticky }) {
  const on = sort.key === k;
  const Icon = !on ? ArrowUpDown : (sort.dir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th scope="col" aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn('whitespace-nowrap px-3 py-2 font-medium', align === 'right' && 'text-right', sticky && 'viz-sticky-col')}>
      <button type="button" onClick={() => onSort(k)} data-testid={testid}
        className={cn('tap inline-flex items-center gap-1 rounded uppercase tracking-wide hover:text-foreground', on && 'text-foreground')}>
        {label}
        <Icon className="h-3 w-3" aria-hidden />
      </button>
    </th>
  );
}

/**
 * The per-slot table (sortable; a row opens that slot's Stats) and the one-hue rate-by-slot chart with the batch average; the table
 * is the chart's twin and is always shown under it. The columns that answer "how is this slot doing" come first (Slot, Rate, Engaged,
 * Followed back), so a phone shows them without a sideways scroll; phone and tracking date last. `batch` (batch scope): the server
 * counts the batch's DETACHED slots in its figures too, so such a row is marked (it engages nobody in batch mode).
 */
export function SlotsCard({ m, batch = null, onOpen, testid = 'fb-slots' }) {
  const stuckBy = useMemo(() => {
    const out = {};
    stuckSlots(batch).forEach((p) => { out[p.slot] = p; });
    return out;
  }, [batch]);
  const rows = useMemo(() => slotRows(m).map((r) => ({ ...r, stuck: stuckBy[r.slot] || null })), [m, stuckBy]);
  const [sort, setSort] = useState({ key: 'slot', dir: 'asc' });
  const sorted = useMemo(() => sortSlotRows(rows, sort.key, sort.dir), [rows, sort]);
  const cov = covered(m, m.cohort.from);
  const average = cov ? rateOf(m.totals.engaged, m.totals.followedBack) : null;
  const srcKeys = m.sourceOrder.filter((k) => rows.some((r) => r.bySource[k] && r.bySource[k].engaged > 0));
  // the change column only when the server sends each slot's previous period (the r16m server does not yet)
  const withChange = rows.some((r) => r.deltaPp !== null);
  const onSort = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: k === 'slot' || k === 'device' ? 'asc' : 'desc' }));
  if (!rows.length) return null;
  const head = (label, k, align) => (
    <SortHeader label={label} k={k} sort={sort} onSort={onSort} align={align} testid={`${testid}-sort-${k}`} sticky={k === 'slot'} />
  );
  const anyStuck = rows.some((r) => r.stuck);
  return (
    <ChartCard icon={Layers} title="By slot" testid={testid}
      subtitle={`Each slot's rate in the matured period; the dashed line is the ${m.kind === 'batch' ? 'batch' : 'group'} average (all its people over all its engagements). `
        + `Click a bar or a row to open that slot's Stats.${anyStuck ? ' A slot marked "detached" or "not a member" engages nobody in batch mode: its figures are from before.' : ''}`}
      after={(
        <div className="mt-4">
          <ScrollX className="viz-table-scroll rounded-lg border border-white/5" testid={`${testid}-scroll`}>
            <table className="w-full text-sm tnum" data-testid={`${testid}-table`}>
              <caption className="sr-only">Follow-back by slot, sortable</caption>
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  {head('Slot', 'slot')}
                  {head('Rate', 'rate', 'right')}
                  {withChange && head('Change', 'deltaPp', 'right')}
                  {head('Engaged', 'engaged', 'right')}
                  {head('Followed back', 'followedBack', 'right')}
                  {srcKeys.map((k) => (
                    <SortHeader key={k} label={shortLabel(k, m.labelOf(k))} k={`src:${k}`} sort={sort}
                      onSort={onSort} align="right" testid={`${testid}-sort-src-${k}`} />
                  ))}
                  {head('Phone', 'device')}
                  {head('Tracking since', 'trackingSince')}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.slot} className="cursor-pointer border-t border-white/5 hover:bg-white/[.04]" onClick={() => onOpen(r.slot)}
                    data-testid={`${testid}-row-${r.slot}`} data-stuck={r.stuck ? r.stuck.status : undefined}>
                    <th scope="row" className="viz-sticky-col whitespace-nowrap px-3 py-1.5 text-left font-medium text-foreground">
                      <button type="button" className="tap hover:underline" onClick={(e) => { e.stopPropagation(); onOpen(r.slot); }}>{r.slot}</button>
                      {r.stuck && (
                        <span className="ml-1.5 rounded-full border border-[hsl(var(--warning)/.5)] px-1.5 text-[11px] font-normal text-[hsl(var(--warning))]"
                          title={r.stuck.status === 'DETACHED' ? 'Detached from the batch: engages nobody in batch mode until joined again' : 'Not a member of the batch: engages nobody in batch mode until joined'}
                          data-testid={`${testid}-stuck-${r.slot}`}>
                          {pendingShort(r.stuck)}
                        </span>
                      )}
                      {!r.signalOk && <span className="ml-1.5 text-[11px] font-normal text-[hsl(var(--warning))]" title="Its follow-back list was not read in the last 24 h">not reporting</span>}
                    </th>
                    <td className={cn('px-3 py-1.5 text-right font-semibold', r.rankable ? 'text-foreground' : 'text-muted-foreground')}>
                      {fmtRatePct(r.rate)}{!r.rankable && r.engaged > 0 ? <span className="ml-1 font-normal">(n {fmtInt(rateN(r))})</span> : null}
                    </td>
                    {withChange && (
                      <td className="whitespace-nowrap px-3 py-1.5 text-right">
                        {r.deltaPp === null ? <span className="text-muted-foreground">-</span> : <Delta value={r.deltaPp} text={fmtPp(r.deltaPp)} className="justify-end text-foreground" />}
                      </td>
                    )}
                    <td className="px-3 py-1.5 text-right text-foreground">{fmtInt(r.engaged)}</td>
                    <td className="px-3 py-1.5 text-right text-foreground">{r.rate === null ? '-' : fmtInt(r.followedBack)}</td>
                    {srcKeys.map((k) => {
                      const c = r.bySource[k];
                      return (
                        <td key={k} className="whitespace-nowrap px-3 py-1.5 text-right text-muted-foreground">
                          <span className="mr-1.5 inline-block h-2 w-2 rounded-[2px] align-middle" style={{ background: sourceColor(chartKeyOf(k)) }} aria-hidden />
                          {c && c.engaged > 0 ? fmtRatePct(c.rate) : '-'}
                        </td>
                      );
                    })}
                    <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">{r.device || '-'}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">{r.trackingSince ? fmtDay(localDay(m, r.trackingSince), 'medium') : 'not yet'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
          <CountsNote m={m} testid={`${testid}-counts`} />
        </div>
      )}>
      <SlotRateChart rows={sortSlotRows(rows, 'slot', 'asc')} average={average} minRankN={m.minRankN} onOpen={onOpen} testid={`${testid}-plot`} />
    </ChartCard>
  );
}

/* ------------------------------------------------------------------ the batch panel */

/*
 * What each action really changes (review 2026-10-20, checked against the r16m server's BatchResolver): a DETACHED slot of a live
 * batch answers CONFLICT DETACHED at every state call while #1216 is on (its own #7 phone is a batch phone, so a new #1217 does not
 * free it); a retired batch's DETACHED rows count as "no batch", so the next state call of a former member with #1216 on CREATES a new
 * batch on these phones, which answers SEEDING until it is seeded again. That new batch does NOT start empty (server review fix
 * c0c82bd, MUST_FIX 1, BatchLedgerStore.carry): Retire keeps the batch as each phone's last_batch_id, and the new batch, inside its
 * creation, COPIES every non-RELEASED row of the retired batch into its own scope as SEEDED (engaged, filtered, still claimed and
 * outcome UNKNOWN alike), plus its members' own rows from any other batch scope. Only a RELEASED row (a release with sends = 0, proof
 * that nothing was sent) is not copied. `ctx.unknown`: the batch's unknown-outcome count when the page shows All time (null otherwise).
 */
const ACTION_TEXT = {
  join: (b, slot, extra) => ({
    title: `Join ${slot} to ${batchName(b)}?`,
    body: [
      `${slot} becomes a member of ${batchName(b)}. ${extra && extra.length ? `Its phones that are not in the batch yet (${extra.join(', ')}) are added to it.`
        : 'Any of its #1217 phones that are not in the batch yet are added to it.'}`,
      'From its next session it claims people in this batch\'s ledger: it skips anyone another member has engaged, and the others skip anyone it engages.',
      'Until the next session of a member on the PC imports its history (and any added phone\'s file), the batch answers SEEDING: no member engages until then. On a one-PC batch that is the next session.',
      'Nothing is engaged by this click. A phone that belongs to another batch makes the server refuse the join.',
    ],
    ok: 'Join',
  }),
  detach: (b, slot) => ({
    title: `Detach ${slot} from ${batchName(b)}?`,
    body: [
      `${slot} stops claiming people in ${batchName(b)}'s ledger. The people it engaged stay in that ledger, so the other members keep skipping them.`,
      `While its #1216 is on, it then engages NOBODY in batch mode: at every session the server answers CONFLICT (DETACHED), because its own phone (#7) is one of the batch's phones. Changing #1217 does not change that.`,
      'To let it engage again: Join it again here, or turn its #1216 off (it then runs outside the batch, without the ledger).',
    ],
    ok: 'Detach',
  }),
  retire: (b, slot, extra, ctx = {}) => ({
    title: `Retire ${batchName(b)}?`,
    body: [
      `All ${b.members.length} members${b.pending.length ? ` and ${b.pending.length} pending or detached ${b.pending.length === 1 ? 'slot' : 'slots'}` : ''} are detached, and the batch's ${b.devices.length} phones are freed. Its ledger stays as history: these figures keep showing it.`,
      'Batch mode does NOT stop: at the next session of any former member that still has #1216 on, the server creates a NEW batch on these phones. It answers SEEDING (no member engages) until a session imports the members\' history and the phones\' files again. To end batch mode, turn #1216 off on those slots first.',
      `That new batch keeps this batch's exclusions: when it takes these phones, it copies everyone in this ledger into its own, so its members skip them. That includes the people whose engagement was sent but never confirmed (outcome unknown${isNum(ctx.unknown) ? `: ${fmtInt(ctx.unknown)} in this batch` : ''}) and the people still claimed. Only a person released because nothing was sent is not copied. It also skips the people in the phones' and accounts' own files.`,
      'To remove one wrong slot, Detach it instead: Retire pauses every member that still has #1216 on until the new batch is seeded, and the wrong slot joins that new batch again while its #1216 is on.',
    ],
    ok: 'Retire the batch',
  }),
};

function Stat({ label, children, testid }) {
  return (
    <div className="min-w-0 rounded-xl border border-white/5 bg-white/[.02] px-3.5 py-2.5" data-testid={testid}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-foreground tnum">{children}</p>
    </div>
  );
}

/**
 * The batch record (GET /follow-back/batches, one of them): members, PENDING / DETACHED slots with the reason and a Join button,
 * phones, "seeded N of M", "on the ledger N of M", warnings, Detach per member and Retire for the batch (each confirmed).
 */
export function BatchPanel({ batch, groupKey, canAct = true, unknown = null, testid = 'fb-batch' }) {
  const qc = useQueryClient();
  const [ask, setAsk] = useState(null);       // { op, slot?, text }
  const mut = useMutation({
    mutationFn: ({ op, slot }) => (op === 'join' ? followBackApi.join(batch.id, slot)
      : op === 'detach' ? followBackApi.detach(batch.id, slot) : followBackApi.retire(batch.id)),
    onSuccess: (res, { op, slot }) => {
      const msg = res && res.data && (res.data.message || res.data.r) ? String(res.data.message || res.data.r) : 'done';
      fbLog('info', `${FB_TAG} batch ${batch.id} ${op}${slot ? ` ${slot}` : ''}: OK (${msg}) at ${new Date().toISOString()}`);
      toast.success(op === 'retire' ? `${batch.label} retired` : `${slot} ${op === 'join' ? 'joined' : 'detached'}`, { description: msg === 'done' ? undefined : msg });
      qc.invalidateQueries({ queryKey: ['followBackBatches'] });
      if (groupKey) qc.invalidateQueries({ queryKey: groupKey });
    },
    onError: (err, { op, slot }) => {
      const text = r14ErrorText(err, 'The server did not answer');
      fbLog('warn', `${FB_TAG} batch ${batch.id} ${op}${slot ? ` ${slot}` : ''}: REFUSED - ${text} at ${new Date().toISOString()}`);
      toast.error(`${op === 'retire' ? 'Retire' : op === 'join' ? 'Join' : 'Detach'} failed`, { description: text });
    },
  });
  const open = (op, slot, extra) => setAsk({ op, slot, text: ACTION_TEXT[op](batch, slot, extra, { unknown }) });
  const retired = !!batch.retired;
  const act = !retired && canAct;
  const oldWorker = batch.members.filter((x) => !x.onLedger);
  const memberDevices = new Set(batch.devices);
  return (
    <ChartCard icon={Layers} title={batch.label} testid={testid}
      badge={retired
        ? <StateChip label={`Retired ${fmtDay(new Date(batch.retired).toISOString().slice(0, 10), 'medium')}`} tone="muted" code="RETIRED" testid={`${testid}-state`} />
        : <StateChip label="Batch mode" tone="ok" code="LIVE" testid={`${testid}-state`} />}
      subtitle={retired
        ? 'This batch was retired: its members were detached and its phones freed. Its figures stay as history.'
        : 'The slots that share one engagement ledger: a person engaged by one member is skipped by the others. Membership changes only here.'}>
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Stat label="Members" testid={`${testid}-members-n`}>{fmtInt(batch.members.length)}</Stat>
        <Stat label="Seeded" testid={`${testid}-seeded`}>
          {isNum(batch.seeded.n) ? `${fmtInt(batch.seeded.n)} of ${fmtInt(batch.seeded.of)}` : '-'}
          {batch.seeded.missing.length > 0 && <span className="block text-xs font-normal text-muted-foreground">MISSING: {batch.seeded.missing.join(', ')}</span>}
        </Stat>
        <Stat label="On the ledger" testid={`${testid}-ledger`}>
          {isNum(batch.onLedger.n) ? `${fmtInt(batch.onLedger.n)} of ${fmtInt(batch.onLedger.of)}` : '-'}
          {oldWorker.length > 0 && !retired && (
            <span className="block text-xs font-normal text-muted-foreground">
              no ledger call in 24 h: {oldWorker.map((x) => `${x.slot}${x.workerVer ? ` (${x.workerVer})` : ''}`).join(', ')}
            </span>
          )}
        </Stat>
        <Stat label="Since" testid={`${testid}-since`}>{batch.since ? fmtDay(new Date(batch.since).toISOString().slice(0, 10), 'medium') : '-'}</Stat>
      </div>
      {!retired && oldWorker.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground text-pretty" data-testid={`${testid}-strict-note`}>
          The batch is strict only when every member runs the new worker: a member that made no ledger call in the last 24 h (an older
          worker, or not running) checks only the phones&apos; files.
        </p>
      )}
      {batch.devices.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`${testid}-devices`}>
          <span className="mr-1 text-xs text-muted-foreground">Phones</span>
          {batch.devices.map((d) => (
            <span key={d} className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-xs text-foreground">
              <Smartphone className="h-3 w-3 text-muted-foreground" aria-hidden />{d}
            </span>
          ))}
        </div>
      )}
      {batch.warnings.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid={`${testid}-warnings`}>
          {batch.warnings.map((w) => (
            <li key={w} className="flex items-start gap-2 text-xs text-foreground">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--warning))]" aria-hidden />{w}
            </li>
          ))}
        </ul>
      )}
      {batch.pending.length > 0 && (
        <div className="mt-4 scroll-mt-20" id={`${testid}-pending`} data-testid={`${testid}-pending`}>
          <h3 className="mb-1.5 text-sm font-semibold text-foreground">Not in the batch ({fmtInt(batch.pending.length)})</h3>
          <ul className="divide-y divide-white/5 rounded-xl border border-[hsl(var(--warning)/.3)] bg-[hsl(var(--warning)/.05)]">
            {batch.pending.map((p) => {
              const extra = p.devices.filter((d) => !memberDevices.has(d));
              return (
                <li key={p.slot} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3.5 py-2.5" data-testid={`${testid}-pending-${p.slot}`}>
                  <span className="font-medium text-foreground">{p.slot}</span>
                  <StateChip label={p.status === 'DETACHED' ? 'Detached' : 'Pending'} tone="warn" code={p.status} />
                  {p.device && <span className="text-xs text-muted-foreground">{p.device}</span>}
                  <span className="min-w-0 flex-1 basis-60 text-xs text-muted-foreground text-pretty">{pendingReason(p, batch)}</span>
                  {act && (
                    <button type="button" onClick={() => open('join', p.slot, extra)} disabled={mut.isPending}
                      className="tap inline-flex h-8 items-center gap-1.5 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                      data-testid={`${testid}-join-${p.slot}`}>
                      <UserPlus2 className="h-3.5 w-3.5" aria-hidden />Join
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {batch.members.length > 0 && (
        <div className="mt-4" data-testid={`${testid}-members`}>
          <h3 className="mb-1.5 text-sm font-semibold text-foreground">Members ({fmtInt(batch.members.length)})</h3>
          <ul className="grid gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
            {batch.members.map((x) => (
              <li key={x.slot} className="flex min-w-0 items-center gap-2 rounded-lg border border-white/5 bg-white/[.02] px-2.5 py-1.5 text-xs"
                data-testid={`${testid}-member-${x.slot}`}>
                {x.onLedger
                  ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--success))]" aria-label="on the ledger" />
                  : <CircleDashed className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--warning))]" aria-label="older worker: not on the ledger" />}
                <span className="font-medium text-foreground">{x.slot}</span>
                <span className="truncate text-muted-foreground">
                  {[x.device, x.seeded ? 'seeded' : 'not seeded', x.workerVer ? x.workerVer : (x.onLedger ? null : 'no ledger call in 24 h')].filter(Boolean).join(' · ')}
                </span>
                {act && (
                  <button type="button" onClick={() => open('detach', x.slot)} disabled={mut.isPending}
                    className="tap ml-auto inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-white/10 px-2 text-[11px] text-muted-foreground hover:bg-white/5 hover:text-foreground disabled:opacity-50"
                    data-testid={`${testid}-detach-${x.slot}`}>
                    <UserMinus className="h-3 w-3" aria-hidden />Detach
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!retired && !canAct && (
        <p className="mt-4 rounded-xl border border-white/10 bg-white/[.03] px-3.5 py-2 text-xs text-muted-foreground text-pretty" data-testid={`${testid}-admin-note`}>
          Viewing as an admin: this list holds every account&apos;s batches, and Join / Detach / Retire act only on the signed-in
          account&apos;s own slots, so they are not offered here. Sign in as the batch&apos;s account to change its membership.
        </p>
      )}
      {act && (
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={() => open('retire')} disabled={mut.isPending}
            className="tap inline-flex h-8 items-center gap-1.5 rounded-full border border-destructive/50 px-3 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-50"
            data-testid={`${testid}-retire`}>
            <Archive className="h-3.5 w-3.5" aria-hidden />Retire this batch
          </button>
        </div>
      )}
      <AlertDialog open={!!ask} onOpenChange={(o) => { if (!o) setAsk(null); }}>
        <AlertDialogContent data-testid={`${testid}-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{ask ? ask.text.title : ''}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground" data-testid={`${testid}-confirm-text`}>
                {ask && ask.text.body.map((t) => <p key={t}>{t}</p>)}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-confirm-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testid}-confirm-ok`}
              className={ask && ask.op === 'retire' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
              onClick={() => { if (ask) mut.mutate({ op: ask.op, slot: ask.slot }); setAsk(null); }}>
              {ask ? ask.text.ok : 'OK'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ChartCard>
  );
}
